<template>
  <template v-if="!loading">
    <vl-grid v-vl-spacer:bottom.medium>
      <vl-column width="10" width-xs="12">
        <vl-title tag-name="h2"> {{ $t('deliverytransformation.detail.title') }} </vl-title>
      </vl-column>
      <vl-column v-if="editStore.canEdit" width="2" width-xs="12">
        <vl-action-group mod-align-right>
          <vl-button
            icon="plus"
            mod-icon-before
            :mod-disabled="editStore.isCreate(DeliveryProcessDefinitionEditType.DeliveryTransformation)"
            @click="editStore.openCreate(DeliveryProcessDefinitionEditType.DeliveryTransformation)"
            >{{ $t('general.add') }}</vl-button
          >
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <vl-grid :class="{ edit: editStore.isCreate(DeliveryProcessDefinitionEditType.DeliveryTransformation) }">
      <vl-column>
        <DeliveryTransformationCreate
          v-if="editStore.isCreate(DeliveryProcessDefinitionEditType.DeliveryTransformation)"
          :definition-id="definitionId"
        />
      </vl-column>
    </vl-grid>
    <vl-grid v-vl-spacer:bottom.medium>
      <vl-column width="12" width-xs="12">
        <vl-accordion-list v-if="items && items.length">
          <vl-accordion-list-item v-for="(transformation, i) in items" :id="i" :key="i">
            <vl-accordion :id="i.toString()" :title="getLabel(transformation)">
              <vl-grid
                :class="{
                  edit: editStore.isEdit(DeliveryProcessDefinitionEditType.DeliveryTransformation, transformation.id)
                }"
              >
                <vl-column width="8">
                  <DeliveryTransformationEdit
                    v-if="editStore.isEdit(DeliveryProcessDefinitionEditType.DeliveryTransformation, transformation.id)"
                    ref="editForm"
                    :definition-id="definitionId"
                    :detail="transformation"
                  />
                  <DeliveryTransformationRead v-else :detail="transformation" />
                </vl-column>
                <vl-column v-if="editStore.canEdit" width="4">
                  <vl-action-group
                    v-if="
                      transformation &&
                      !editStore.isEdit(DeliveryProcessDefinitionEditType.DeliveryTransformation, transformation.id)
                    "
                    mod-align-right
                    mod-space
                    between
                  >
                    <vl-button
                      icon="pencil"
                      mod-icon-before
                      mod-secondary
                      :mod-disabled="editStore.editing"
                      @click="
                        editStore.openEdit(DeliveryProcessDefinitionEditType.DeliveryTransformation, transformation.id)
                      "
                      >{{ $t('general.edit') }}</vl-button
                    >
                    <vl-button icon="trash" mod-icon-before mod-error @click="showDeleteModal(transformation.id)">{{
                      $t('general.delete')
                    }}</vl-button>
                  </vl-action-group>
                  <vl-action-group v-else mod-align-right mod-space between>
                    <vl-button icon="save" x mod-icon-before :mod-loading="reloading" @click="save">{{
                      $t('general.save')
                    }}</vl-button>
                    <vl-button icon="cross" mod-icon-before mod-secondary mod-error @click="editStore.closeEdit">
                      {{ $t('general.cancel') }}
                    </vl-button>
                  </vl-action-group>
                </vl-column>
              </vl-grid>
            </vl-accordion>
          </vl-accordion-list-item>
        </vl-accordion-list>
        <template v-else>
          <vl-annotation>{{ $t('deliverytransformation.detail.empty') }}</vl-annotation>
        </template>
      </vl-column>
    </vl-grid>
  </template>
</template>
<script lang="ts" setup>
import DeliveryTransformationRead from './DeliveryTransformationRead.vue';
import DeliveryTransformationCreate from './DeliveryTransformationCreate.vue';
import DeliveryTransformationEdit from './DeliveryTransformationEdit.vue';
import { DeliveryTransformationDetailDto } from '@/api/backoffice/clients';
import { useDeliveryTransformationStore } from '@/modules/configuration/store/delivery-transformation/delivery-transformation.store';
import {
  DeliveryProcessDefinitionEditType,
  useDeliveryProcessDefinitionEditStore
} from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-edit.store';
import { computed, onMounted, ref, Ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useModalStore } from '@/modules/core/store/modal.store';

const { t } = useI18n();

const store = useDeliveryTransformationStore();
const editStore = useDeliveryProcessDefinitionEditStore();
const modalStore = useModalStore();

const editForm: Ref<typeof DeliveryTransformationEdit | (typeof DeliveryTransformationEdit)[]> = ref(null);

const route = useRoute();

const loading = computed((): boolean => {
  return store.loading;
});

const items = computed((): DeliveryTransformationDetailDto[] => {  
  const unsortedItems = store.byDefinition(definitionId.value);
  return unsortedItems.sort((a, b) => getLabel(a).localeCompare(getLabel(b)));
});


const definitionId = computed((): number => {
  return parseInt(route.params.id as string, 10);
});

const reloading = computed((): boolean => {
  return store.reloading;
});

onMounted(async () => {
  await store.getByDefinition(definitionId.value);
});

const save = () => {
  if (Array.isArray(editForm?.value)) {
    editForm.value[0].onSubmit();
  } else {
    editForm.value.onSubmit();
  }
};

const showDeleteModal = (transformationId: number) => {
  modalStore.showDeleteModal({
    title: t('deliverytransformation.detail.delete.title'),
    props: {
      confirm: () => store.remove(definitionId.value, transformationId)
    }
  });
};

const getLabel = (item: DeliveryTransformationDetailDto): string => {
  const unknown = '(?)';
  const from = `${item.fromEntity?.code ?? unknown}`;
  const to = `${item.toEntity?.code ?? unknown}`;
  const type = item.transformationType?.name ?? '/';

  if (from == unknown && to == unknown) {
    return type;
  }

  return t('deliverytransformation.detail.linkTo', { from, to, type });
};
</script>
<style lang="scss" scoped>
:deep(.vl-accordion__panel) {
  padding-right: 0;
}
</style>
